import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

export interface AuthUser {
  id: string;
  email: string;
  username: string;
  role: 'admin' | 'user';
  totpEnabled: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  session: Session | null;
  initialized: boolean;
  isAuthenticated: boolean;
  emailConfirmed: boolean;
  twoFactorVerified: boolean;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  session: null,
  initialized: false,
  isAuthenticated: false,
  emailConfirmed: false,
  twoFactorVerified: false,
  logout: async () => {},
});

function buildAuthUserFromToken(u: User): AuthUser {
  const meta = u.user_metadata ?? {};
  const appMeta = (u as unknown as { app_metadata?: Record<string, unknown> }).app_metadata ?? {};
  const role = (appMeta.role as 'admin' | 'user') || (meta.role as 'admin' | 'user') || 'user';
  return {
    id: u.id,
    email: u.email ?? '',
    username: (meta.username as string) || (u.email ?? '').split('@')[0],
    role,
    totpEnabled: false,
  };
}

async function fetchProfileBackground(
  userId: string,
  onResult: (data: { role: 'admin' | 'user'; username: string; totpEnabled: boolean }) => void,
): Promise<void> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 6000);
    const { data, error } = await supabase
      .from('profiles')
      .select('role, username, totp_enabled')
      .eq('id', userId)
      .maybeSingle()
      .abortSignal(controller.signal);
    clearTimeout(timer);
    if (!error && data) {
      onResult({
        role: (data.role as 'admin' | 'user') ?? 'user',
        username: data.username ?? '',
        totpEnabled: data.totp_enabled ?? false,
      });
    }
  } catch {
    // Silently ignore — token-based role is already set
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [initialized, setInitialized] = useState(false);
  const dbRoleRef = useRef<Map<string, { role: 'admin' | 'user'; totpEnabled: boolean }>>(new Map());
  const profileFetchRef = useRef<string | null>(null);

  // Check if 2FA was verified in this browser session
  const twoFactorVerified = sessionStorage.getItem('2fa_verified') === 'true';

  const applyUser = (u: User | null, sess: Session | null) => {
    setSession(sess);
    if (!u) {
      setUser(null);
      profileFetchRef.current = null;
      return;
    }

    const tokenUser = buildAuthUserFromToken(u);
    const confirmed = dbRoleRef.current.get(u.id);
    const initialUser: AuthUser = {
      ...tokenUser,
      role: confirmed?.role ?? tokenUser.role,
      totpEnabled: confirmed?.totpEnabled ?? false,
    };
    setUser(initialUser);

    if (profileFetchRef.current !== u.id) {
      profileFetchRef.current = u.id;
      fetchProfileBackground(u.id, (profile) => {
        dbRoleRef.current.set(u.id, { role: profile.role, totpEnabled: profile.totpEnabled });
        setUser(prev => {
          if (!prev || prev.id !== u.id) return prev;
          return {
            ...prev,
            role: profile.role,
            username: profile.username || prev.username,
            totpEnabled: profile.totpEnabled,
          };
        });
      });
    }
  };

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (!mounted) return;
      applyUser(s?.user ?? null, s);
      setInitialized(true);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      if (!mounted) return;
      applyUser(s?.user ?? null, s);
      setInitialized(true);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const logout = async () => {
    profileFetchRef.current = null;
    dbRoleRef.current.clear();
    sessionStorage.removeItem('2fa_verified');
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
  };

  const emailConfirmed = !!session?.user?.email_confirmed_at;

  return (
    <AuthContext.Provider value={{
      user, session, initialized,
      isAuthenticated: !!user,
      emailConfirmed,
      twoFactorVerified,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): AuthContextValue {
  return useContext(AuthContext);
}
